import { PlayerConfig, VideoJsPlayer } from '@/interfaces/player-config.interface';
import videojs from 'video.js';

/**
 * Player/iFrame API
 * send player event to parent window
 * https://develop.earthtv.com/jira/browse/WV-22
 */
class IFrameService {
  private parentHost: string = '';
  private parentOrigin: string = '';
  private parentElementId: string = '';
  private parentPathName: string = '';
  private handshakeSuccess = false;

  /**
   * Sets weather or not the powerlink is visible etc
   * @param playerConfig
   */
  public initParent(playerConfig: PlayerConfig) {
    if (typeof window.postMessage !== 'undefined' && this.parentOrigin !== '' && this.handshakeSuccess) {
      const message = {
        type: 'setData',
        powerlink: playerConfig.advertisement.enabled,
        accountId: playerConfig.accountId,
        sourceId: playerConfig.sourceId,
        adTargetTag: playerConfig.adTargetTag,
      };
      parent.postMessage(message, this.parentOrigin);
      this.sendStateChangeEvent(playerConfig);
    }
  }

  public handshake(): Promise<string> {
    return new Promise((resolve) => {
      const wait = setTimeout(() => {
        clearTimeout(wait);
        const parentHost = window.location !== window.parent.location ? document.referrer : document.location.href;
        const url = new URL(parentHost);
        this.parentHost = url.host;
        this.parentOrigin = url.origin;
        this.parentPathName = url.pathname;
        resolve(this.parentHost);
      }, 5000);

      window.addEventListener('message', (e) => {
        if (e.data && e.data.type === 'handshake') {
          this.handshakeSuccess = true;
          clearTimeout(wait);
          this.parentHost = e.data.parentHost;
          this.parentOrigin = e.data.parentOrigin;
          this.parentElementId = e.data.parentElementId;
          this.parentPathName = e.data.parentPathName;
          resolve(this.parentOrigin + this.parentPathName );
        }
      });
    });
  }

  /**
   * Send Player events to iframe
   * @param event
   * @param metadata
   */
  public sendStateChangeEvent(playerConfig: PlayerConfig, player?: VideoJsPlayer) {
    // send event and meta data to parent
    if (this.handshakeSuccess) {
      let country = '';
      let city = '';

      if (playerConfig && playerConfig.location && playerConfig.location.country) {
        country = playerConfig.location.country[playerConfig.language];
        city = playerConfig.location.city[playerConfig.language];
      }

      const data = {
        playerId: this.parentElementId,
        isLive: playerConfig.live,
        country,
        city,
        isPaused: player ? player.paused() : true,
      };
      const message = {
        type: 'etvp_onStateChange',
        metadata: data,
      };

      parent.postMessage(message, this.parentOrigin);
    }
  }

  /**
   * Request consent status from parent page
   * Parent page should respond with { type: 'consentStatus', consent: {...} }
   */
  public requestConsent() {
    if (this.handshakeSuccess && typeof window.postMessage !== 'undefined' && this.parentOrigin !== '') {
      const message = {
        type: 'getConsent',
      };
      parent.postMessage(message, this.parentOrigin);
    }
  }

  /**
   * Setup listener for consent updates from parent page
   */
  public setupConsentListener(callback: (consent: any) => void) {
    window.addEventListener('message', (e) => {
      if (e.data && e.data.type === 'consentStatus' && e.data.consent) {
        callback(e.data.consent);
      }
    });
  }
}

export const iFrameService = new IFrameService();
